import { Post, Faculty, University, UniversityCreateResult } from "./posts"
import { Comment, Subject } from "./comments"
import { Subject as CatalogSubject, SubjectStudyYearLink, SubjectTeacherLink } from "./subjects"
import { SubjectComment } from "./subjectComments"
import { KoloNaukowe, KoloNaukoweComment } from "./kolaNaukowe"
import { ExamQuestion } from "./examQuestions"
import { Result } from "./generics"

// Types
type CreateCommentPayload = Pick<Comment, "rating" | "body" | "status" | "would_take_again" | "exam_rating" | "exam_would_take_again" | "exam_body" | "class_difficulty" | "exam_difficulty">
    & { subject_id: number | null; resolved_faculty_id?: number | null };
type CreateSubjectCommentPayload = Pick<SubjectComment, "rating" | "difficulty" | "body" | "would_take_again" | "academic_year">;
type CreateKoloNaukoweCommentPayload = Pick<KoloNaukoweComment, "rating" | "body" | "would_take_again">;
type CreatePostPayload = Pick<Post, "name" | "surname"> & { faculty_id: number };
// Model, do którego wolno doczepić e-mail PO fakcie (patrz setNotifyEmail niżej i
// renderPostHocNotifyPrompt w notifyEmail.ts) - te same klucze co NOTIFY_EMAIL_MODELS w
// backendzie (pages/api/moderation_notify.py).
export type NotifyModelKey =
    "university" | "faculty" | "post" | "subject" | "comment" | "subject_comment"
    | "kolo_naukowe" | "kolo_naukowe_comment" | "exam_question" | "subject_teacher";
// Odpowiedź 202 - zgłoszenie przyjęte, ale zmiana czeka na moderację (patrz _pending_response w
// pages/api/subject_teachers.py).
// Podpowiedź AI dla autora opinii PRZED zapisem (patrz PostCommentView.create w
// pages/api/comments.py) - komentarz NIE został zapisany, autor może poprawić treść albo wysłać
// bez zmian (confirm_unmoderated=true -> ręczna moderacja, jak dawniej).
export interface CommentRevisionProblem {
    fragment: string | null;
    // RODO_ART9 / WULGARYZM / OSKARZENIE_O_NIEUCZCIWOSC / NIE_NA_TEMAT (patrz REVISION_RULES w comments.ts)
    category: string;
    // Krótka, konkretna podpowiedź od AI, co zmienić (bez tłumaczenia zasad).
    fix: string;
}
// Nazwa przedmiotu odrzucona przez AI (patrz PostSubjectCreateView) - nic nie zapisano.
export interface SubjectNameRevision {
    needs_revision: true;
    name: string;
    category: "WULGARYZM" | "NIE_PRZEDMIOT" | "NIEPRAWDZIWA_NAZWA";
    suggested_name: string;
}

export interface CommentRevisionHint {
    problems: CommentRevisionProblem[];
    // Odsyłany przy następnej wysyłce formularza - backend ustala wtedy wynik zdarzenia
    // (CommentRevisionEvent) do maila informacyjnego dla admina.
    revisionEventId: number;
}
export interface PendingChangeResponse {
    pending: true;
    id: number;
    textmessage: string;
}
interface SubjectsResponse {
    subjects: Subject[];
}
interface SubjectCatalogResponse {
    subjects: CatalogSubject[];
}
interface SubjectCommentsResponse {
    comments: SubjectComment[];
}
interface KolaNaukoweResponse {
    kola_naukowe: KoloNaukowe[];
}
interface KoloNaukoweCommentsResponse {
    comments: KoloNaukoweComment[];
}
interface ExamQuestionsResponse {
    questions: ExamQuestion[];
}
interface FacultiesResponse {
    faculties: Faculty[];
    // Ustawione tylko gdy zapytanie filtrowało po ?university= - patrz University.no_faculties.
    no_faculties: boolean;
}
interface UniversitiesResponse {
    universities: University[];
}

// Functions
export function getCsrfToken(): string {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : "";
}

export async function apiFetch<T>(
    method: string,
    path: string,
    body?: unknown
): Promise<Result<T>> {
    try {
        const options: RequestInit = {
            method,
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken":  getCsrfToken(),
            },
        };
        if (body !== undefined) {
            options.body = JSON.stringify(body);
        }

        const res = await fetch(path, options);

        if (!res.ok) {
            let message = `HTTP ${res.status}`;
            try {
                const err = await res.json();
                message = err.error ?? message;
            } catch { /* ignore */ }
            return { ok: false, error: message, status: res.status };
        }

        if (res.status === 204) {
            return { ok: true, data: {} as T };
        }

        const data: T = await res.json();
        return { ok: true, data };

    } catch (err) {
        return { ok: false, error: String(err) };
    }
}

class BlogApiClient {
    async getPosts(universitySlug?: string, facultySlug?: string): Promise<Result<{ posts: Post[] }>> {
        const params = new URLSearchParams();
        if (universitySlug) params.set("university", universitySlug);
        if (facultySlug) params.set("faculty", facultySlug);
        const query = params.toString() ? `?${params.toString()}` : "";
        return apiFetch("GET", `/api/posts/${query}`);
    }

    async getFaculties(universitySlug?: string): Promise<Result<FacultiesResponse>> {
        const query = universitySlug ? `?university=${encodeURIComponent(universitySlug)}` : "";
        return apiFetch("GET", `/api/faculties/${query}`);
    }

    async getUniversities(): Promise<Result<UniversitiesResponse>> {
        return apiFetch("GET", `/api/universities/`);
    }

    // force - "to inna uczelnia, dodaj mimo to" po propozycji istniejącej (patrz universityPicker.ts).
    async createUniversity(name: string, force = false): Promise<Result<UniversityCreateResult>> {
        return apiFetch("POST", `/api/universities/`, { name, force });
    }

    async getPost(id: number): Promise<Result<Post>> {
        return apiFetch("GET", `/api/posts/${id}/`);
    }

    async getPostsByIds(ids: number[]): Promise<Result<{ posts: Post[] }>> {
        return apiFetch("GET", `/api/posts/compare/?ids=${ids.join(",")}`);
    }

    async getComments(postId: number): Promise<Result<{ comments: Comment[] }>> {
        return apiFetch("GET", `/api/posts/${postId}/comments/`);
    }

    async getSubjects(facultyId?: number): Promise<Result<SubjectsResponse>> {
        const query = facultyId !== undefined ? `?faculty=${facultyId}` : "";
        return apiFetch("GET", `/api/subjects/${query}`);
    }

    // Odrzucona przez AI nazwa NIE jest zapisywana - wraca SubjectNameRevision (patrz
    // PostSubjectCreateView). confirmModeration = "to prawdziwy przedmiot, wyślij do sprawdzenia".
    async createSubject(postId: number, name: string, confirmModeration = false): Promise<Result<Subject | SubjectNameRevision>> {
        return apiFetch("POST", `/api/posts/${postId}/subjects/`, confirmModeration ? { name, confirm_moderation: true } : { name });
    }

    // Katalog modułu "Przedmioty" (przedmioty obieralne/fakultatywne oznaczone
    // in_course_catalog=True - patrz ?catalog=1 w SubjectListView.get_queryset), w odróżnieniu
    // od getSubjects wyżej (subject-picker w formularzu opinii o wykładowcy, wszystkie przedmioty
    // wydziału, bez kategorii/ocen).
    async getSubjectsCatalog(facultyId: number): Promise<Result<SubjectCatalogResponse>> {
        return apiFetch("GET", `/api/subjects/?faculty=${facultyId}&catalog=1`);
    }

    async createSubjectCatalogEntry(name: string, facultyId: number): Promise<Result<{ subject: CatalogSubject, textmessage: string }>> {
        return apiFetch("POST", `/api/subjects/`, { name, faculty_id: facultyId });
    }

    async getSubjectBySlug(slug: string): Promise<Result<CatalogSubject & { rating_distribution: { [key: string]: number } }>> {
        return apiFetch("GET", `/api/subjects/${slug}/`);
    }

    // Kto prowadzi przedmiot / do jakiego roku studiów należy (patrz SubjectTeacher/
    // SubjectStudyYear w models.py) - CELOWO bez odpowiednika "update", tylko create/delete
    // (każdy wpis to jeden wiersz do dodania albo usunięcia, nie ma czego "edytować w miejscu").
    // Zmiana listy prowadzących: 202 + PendingChangeResponse, gdy czeka na zatwierdzenie admina;
    // 201/200 z SubjectTeacherLink, gdy weszła od razu (WPiA) albo prowadzący już jest na liście.
    async addSubjectTeacher(subjectId: number, postId: number): Promise<Result<SubjectTeacherLink | PendingChangeResponse>> {
        return apiFetch("POST", `/api/subjects/${subjectId}/teachers/`, { post_id: postId });
    }

    // 204 (pusty obiekt) - usunięte od razu (Faculty.teacher_edits_auto_approve, np. WPiA);
    // 202 - czeka na zatwierdzenie admina.
    async removeSubjectTeacher(linkId: number): Promise<Result<PendingChangeResponse | {}>> {
        return apiFetch("DELETE", `/api/subject-teachers/${linkId}/`);
    }

    async addSubjectStudyYear(subjectId: number, yearNumber: number): Promise<Result<SubjectStudyYearLink>> {
        return apiFetch("POST", `/api/subjects/${subjectId}/study-years/`, { year_number: yearNumber });
    }

    async removeSubjectStudyYear(linkId: number): Promise<Result<{}>> {
        return apiFetch("DELETE", `/api/subject-study-years/${linkId}/`);
    }

    async getSubjectComments(subjectId: number): Promise<Result<SubjectCommentsResponse>> {
        return apiFetch("GET", `/api/subjects/${subjectId}/comments/`);
    }

    async createSubjectComment(
        subjectId: number, payload: CreateSubjectCommentPayload
    ): Promise<Result<{ comment: SubjectComment, textmessage: string }>> {
        return apiFetch("POST", `/api/subjects/${subjectId}/comments/`, payload);
    }

    async voteSubjectComment(commentId: number, upvoteDelta: number, downvoteDelta: number): Promise<boolean> {
        const res = await apiFetch("POST", `/api/subject-comments/${commentId}/vote/`, {
            upvote_delta: upvoteDelta, downvote_delta: downvoteDelta,
        });
        return res.ok;
    }

    async reportSubjectComment(
        commentId: number,
        payload: { reason: string; explanation: string; reporter_name: string; email: string; good_faith_confirmed: boolean }
    ): Promise<Result<{}>> {
        return apiFetch("POST", `/api/subject-comments/${commentId}/report/`, payload);
    }

    async getKolaNaukowe(facultyId: number): Promise<Result<KolaNaukoweResponse>> {
        return apiFetch("GET", `/api/kola-naukowe/?faculty=${facultyId}`);
    }

    async createKoloNaukowe(
        name: string, description: string, facultyId: number
    ): Promise<Result<{ kolo_naukowe: KoloNaukowe, textmessage: string }>> {
        return apiFetch("POST", `/api/kola-naukowe/`, { name, description, faculty_id: facultyId });
    }

    async getKoloNaukoweBySlug(slug: string): Promise<Result<KoloNaukowe & { rating_distribution: { [key: string]: number } }>> {
        return apiFetch("GET", `/api/kola-naukowe/${slug}/`);
    }

    async getKoloNaukoweComments(koloId: number): Promise<Result<KoloNaukoweCommentsResponse>> {
        return apiFetch("GET", `/api/kola-naukowe/${koloId}/comments/`);
    }

    async createKoloNaukoweComment(
        koloId: number, payload: CreateKoloNaukoweCommentPayload
    ): Promise<Result<{ comment: KoloNaukoweComment, textmessage: string }>> {
        return apiFetch("POST", `/api/kola-naukowe/${koloId}/comments/`, payload);
    }

    async getExamQuestions(postId: number): Promise<Result<ExamQuestionsResponse>> {
        return apiFetch("GET", `/api/posts/${postId}/exam-questions/`);
    }

    async createExamQuestion(postId: number, payload: { text: string }): Promise<Result<{ questions: ExamQuestion[], textmessage: string }>> {
        return apiFetch("POST", `/api/posts/${postId}/exam-questions/`, payload);
    }

    async createFaculty(name: string, universitySlug: string): Promise<Result<Faculty>> {
        return apiFetch("POST", `/api/faculties/`, { name, university_slug: universitySlug });
    }

    // Doczepia opcjonalny e-mail do JUŻ zapisanego zgłoszenia (uczelnia/wydział/wykładowiec/
    // przedmiot/komentarz) - patrz SetNotifyEmailView w backendzie i renderPostHocNotifyPrompt w
    // notifyEmail.ts. Formularze "dodaj X" celowo NIE zbierają maila z góry (patrz historia tej
    // funkcji) - dopiero po udanym dodaniu/zgłoszeniu pojawia się osobny, opcjonalny krok.
    async setNotifyEmail(model: NotifyModelKey, pk: number, email: string): Promise<Result<{ success: true }>> {
        return apiFetch("POST", `/api/moderation/notify-email/`, { model, pk, email });
    }

    async createPost(payload: CreatePostPayload): Promise<Result<{ post: Post, textmessage: string }>> {
        return apiFetch("POST", `/api/posts/`, payload);
    }

    async createComment(
        postId: number,
        payload: CreateCommentPayload & { confirm_unmoderated?: boolean; revision_event_id?: number }
    ): Promise<Result<{comment: Comment, textmessage: string, revision?: CommentRevisionHint}>> {

        const response = await apiFetch<{comment: Comment, textmessage: string} | { needs_revision: true; problems: CommentRevisionProblem[]; revision_event_id: number }>(
            "POST", `/api/posts/${postId}/comments/`, payload,
        );
        
        if (!response.ok){
            return {
                ok: false,
                error : response.error
            }
        }
        else if ("needs_revision" in response.data) {
            // Nic nie zostało zapisane - patrz podpowiedź przed zapisem w PostCommentView.create.
            return { ok: true, data: { comment: undefined as unknown as Comment, textmessage: "", revision: { problems: response.data.problems, revisionEventId: response.data.revision_event_id } } };
        }
        else{
            return {
                ok: true,
                data: {
                    comment : response.data.comment,
                    textmessage : response.data.textmessage
                }
            }
        }   
    }

    async submitVote(
        commentId: number,
        upvoteDelta: number,
        downvoteDelta: number
    ): Promise<boolean> {
        const res = await apiFetch(
            "POST",`/api/comments/${commentId}/vote/`,{
                "upvote_delta": upvoteDelta,
                "downvote_delta": downvoteDelta
            }
        );
        return res.ok;
    }

    async reportComment(
        commentId: number,
        payload: {
            reason: string; explanation: string; reporter_name: string; email: string;
            good_faith_confirmed: boolean;
        }
    ): Promise<Result<{}>> {
        return apiFetch("POST", `/api/comments/${commentId}/report/`, payload);
    }

    async sendContactMessage(payload: {
        category: string, message: string, email: string
    }): Promise<Result<{ textmessage: string }>> {
        return apiFetch("POST", `/api/contact/`, payload);
    }

}
export async function recordPageView(postId: number): Promise<void> {
    const storageKey = `viewed_post_${postId}`;
    // Jeśli w tej sesji użytkownik już tu był, nic nie robimy
    if (sessionStorage.getItem(storageKey)) return;

    sessionStorage.setItem(storageKey, "1");
    await apiFetch<void> ("POST",`/api/posts/${postId}/view/`);
}


export const api = new BlogApiClient();
